import { inject, Injectable } from '@angular/core';
import { CookiesService } from './cookies.service';
import {
  IPromoBoxCookie,
  IPromoBoxCookieWithExpiration,
  IPromoBoxItem,
  PROMO_BOX_PAGES,
  PromoBoxType,
} from '../interfaces/promo-box';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import { IAcademyBookItem } from '../interfaces/academy-library';
import { IAcademyVideosItem } from '../interfaces/academy-video';
import { IBenefitsItem } from '../interfaces/benefits';
import { IEvent } from '../interfaces/event';
import { ICmsNewsList, INewsBase } from '../interfaces/news';
import { EventsService } from './events.service';
import { NewsContentfulService } from './news-contentful.service';
import { GlobalAppConfigFacadeService } from './facades/global-app-config-facade.service';
import { AcademyContentfulService } from './academy-contentful.service';
import { BenefitsContentfulService } from './benefits-contentful.service';
import { cloneDeep } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class PromoBoxService {
  eventsService = inject(EventsService);
  globalAppConfigFacadeService = inject(GlobalAppConfigFacadeService);
  newsContentfulService = inject(NewsContentfulService);
  academyContentfulService = inject(AcademyContentfulService);

  benefitsContentfulService = inject(BenefitsContentfulService);
  cookiesService = inject(CookiesService);

  promoCookieName = 'promo-box-seen';
  eachCategoryDrawCount = 5;
  promoBoxItemsCount = 3;

  news$: Observable<ICmsNewsList[]>;
  events$: Observable<IEvent[]>;
  books$: Observable<IAcademyBookItem[]>;
  videos$: Observable<IAcademyVideosItem[]>;
  benefits$: Observable<IBenefitsItem[]>;

  pagesPromoItems: IPromoBoxItem[] = cloneDeep(PROMO_BOX_PAGES);

  defaultCookieValue: IPromoBoxCookie = {
    [PromoBoxType.AcademyBook]: [],
    [PromoBoxType.AcademyVideo]: [],
    [PromoBoxType.Benefit]: [],
    [PromoBoxType.Event]: [],
    [PromoBoxType.News]: [],
  };

  private _chosenPromos: IPromoBoxItem[] = [];
  private _loading$ = new BehaviorSubject<boolean>(false);

  get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  getPromoBoxCookie(): IPromoBoxCookieWithExpiration {
    const cookieValue = this.cookiesService.getCookie(this.promoCookieName);
    return cookieValue ? JSON.parse(cookieValue) : { ...this.defaultCookieValue, expirationDate: '' };
  }

  init() {
    const promoBoxCookie = this.getPromoBoxCookie();
    this._loading$.next(true);

    this.events$ = this.eventsService
      .getEvents('upcoming', 0, this.eachCategoryDrawCount)
      .pipe(
        map((response) =>
          response.Events.filter((event) => !promoBoxCookie[PromoBoxType.Event]?.includes(`${event.Id}`)).slice(
            0,
            this.eachCategoryDrawCount
          )
        )
      );

    this.news$ = this.globalAppConfigFacadeService.legalEntityCountryFeature$.pipe(
      take(1),
      switchMap((legalEntityFeature) =>
        this.newsContentfulService
          .getNewsByLegalEntityCountryFeature(legalEntityFeature, 0, this.eachCategoryDrawCount, false)
          .pipe(
            map((response) =>
              response.news
                .filter((newsItem) => !promoBoxCookie[PromoBoxType.News]?.includes(`${newsItem.Id}`))
                .slice(0, this.eachCategoryDrawCount)
            )
          )
      )
    );

    this.books$ = this.academyContentfulService
      .getAcademyBooks$(null, this.eachCategoryDrawCount, 0)
      .pipe(
        map((response) =>
          response.books
            .filter((book) => !promoBoxCookie[PromoBoxType.AcademyBook]?.includes(`${book.id}`))
            .slice(0, this.eachCategoryDrawCount)
        )
      );

    this.videos$ = this.academyContentfulService
      .getAcademyVideoItems$(null, this.eachCategoryDrawCount, 0)
      .pipe(
        map((response) =>
          response.videos
            .filter((video) => !promoBoxCookie[PromoBoxType.AcademyVideo]?.includes(`${video.id}`))
            .slice(0, this.eachCategoryDrawCount)
        )
      );

    this.benefits$ = this.benefitsContentfulService
      .getBenefits$({ limit: this.eachCategoryDrawCount }, false)
      .pipe(
        map((response) =>
          response.items
            .filter((benefit) => !promoBoxCookie[PromoBoxType.Benefit]?.includes(`${benefit.id}`))
            .slice(0, this.eachCategoryDrawCount)
        )
      );

    forkJoin([
      this.streamErrorWrapper(this.events$, PromoBoxType.Event),
      this.streamErrorWrapper(this.news$, PromoBoxType.News),
      this.streamErrorWrapper(this.books$, PromoBoxType.AcademyBook),
      this.streamErrorWrapper(this.videos$, PromoBoxType.AcademyVideo),
      this.streamErrorWrapper(this.benefits$, PromoBoxType.Benefit),
    ])
      .pipe(
        map(([events, news, books, videos, benefits]) => {
          const allPromos = [
            ...events.map((event) => this.mapToPromoBoxItem(event, PromoBoxType.Event)),
            ...news.map((newsItem) => this.mapToPromoBoxItem(newsItem, PromoBoxType.News)),
            ...books.map((book) => this.mapToPromoBoxItem(book, PromoBoxType.AcademyBook)),
            ...videos.map((video) => this.mapToPromoBoxItem(video, PromoBoxType.AcademyVideo)),
            ...benefits.map((benefit) => this.mapToPromoBoxItem(benefit, PromoBoxType.Benefit)),
            ...this.pagesPromoItems,
          ];

          return allPromos
            .sort(() => 0.5 - Math.random())
            .slice(0, this.promoBoxItemsCount)
            .map((promo, idx) => ({
              ...promo,
              image: promo.image ? promo.image : `/assets/images/promo-box/${promo.type}/${idx + 1}.png`,
            }));
        })
      )
      .subscribe((promos) => {
        this._chosenPromos = promos;
        this._loading$.next(false);
      });
  }

  updatePromoBoxCookie(value: IPromoBoxCookie): void {
    const updatedCookie: IPromoBoxCookieWithExpiration = {
      [PromoBoxType.AcademyBook]: [],
      [PromoBoxType.AcademyVideo]: [],
      [PromoBoxType.Benefit]: [],
      [PromoBoxType.Event]: [],
      [PromoBoxType.News]: [],
      // If there is no cookie or the cookie is expired, we set a new cookie with the current values and a new expiration date ahead of 365 days.
      expirationDate:
        this.getPromoBoxCookie()?.expirationDate ||
        new Date(new Date().getTime() + 365 * 24 * 60 * 60 * 1000).toUTCString(),
    };

    const existingCookie = this.getPromoBoxCookie() || {};

    Object.keys(updatedCookie).forEach((key) => {
      if (key === 'expirationDate') {
        return;
      }

      updatedCookie[key] = [...(existingCookie[key] || []), ...(value[key] || [])];
    });

    this.cookiesService.setCookieWithExpirationString(
      this.promoCookieName,
      JSON.stringify(updatedCookie),
      updatedCookie.expirationDate
    );
  }

  getPromos(): IPromoBoxItem[] {
    if (this._chosenPromos.length !== 0) {
      this.updatePromoBoxCookie(this.prepareCookieFromChosenItems(this._chosenPromos));
    }

    return this._chosenPromos;
  }

  private mapToPromoBoxItem(item: unknown, type: PromoBoxType): IPromoBoxItem {
    switch (type) {
      case PromoBoxType.Event:
        return this.mapEventToPromoBoxItem(item as IEvent);
      case PromoBoxType.News:
        return this.mapNewsToPromoBoxItem(item as INewsBase);
      case PromoBoxType.AcademyBook:
        return this.mapBookToPromoBoxItem(item as IAcademyBookItem);
      case PromoBoxType.AcademyVideo:
        return this.mapVideoToPromoBoxItem(item as IAcademyVideosItem);
      case PromoBoxType.Benefit:
        return this.mapBenefitToPromoBoxItem(item as IBenefitsItem);
      default:
        throw new Error(`Unsupported promo box item type: ${type}`);
    }
  }

  private mapEventToPromoBoxItem(event: IEvent): IPromoBoxItem {
    return {
      id: event.Id.toString(),
      type: PromoBoxType.Event,
      title: event.Title,
      description: event.Description,
      url: `/events/${event.Id}`,
      image: null,
      date: `${event.StartDate}${event.EndDate ? ` - ${event.EndDate}` : ''}`,
    };
  }

  private mapNewsToPromoBoxItem(news: INewsBase): IPromoBoxItem {
    return {
      id: news.Id.toString(),
      type: PromoBoxType.News,
      title: news.Title,
      description: news.Description,
      url: `/content/news/${news.Id}`,
      image: null,
      date: news.Published,
    };
  }

  private mapBookToPromoBoxItem(book: IAcademyBookItem): IPromoBoxItem {
    return {
      id: book.id,
      type: PromoBoxType.AcademyBook,
      title: book.title,
      description: book.content,
      url: `/learning-and-dev/academy/library/book/${book.id}`,
      image: null,
      date: null,
    };
  }

  private mapVideoToPromoBoxItem(video: IAcademyVideosItem): IPromoBoxItem {
    return {
      id: video.id,
      type: PromoBoxType.AcademyVideo,
      title: video.title,
      description: video.content,
      url: `/learning-and-dev/academy/videos/${video.id}`,
      image: null,
      date: null,
    };
  }

  private mapBenefitToPromoBoxItem(benefit: IBenefitsItem): IPromoBoxItem {
    return {
      id: benefit.id,
      type: PromoBoxType.Benefit,
      title: benefit.name,
      description: benefit.description,
      url: `/benefits/offers/${benefit.id}`,
      image: null,
      date: null,
    };
  }

  private prepareCookieFromChosenItems(chosenItems: IPromoBoxItem[]): IPromoBoxCookie {
    const cookieValue: IPromoBoxCookie = cloneDeep(this.defaultCookieValue);

    const filteredChosenItems = chosenItems.filter((item) =>
      [
        PromoBoxType.AcademyBook,
        PromoBoxType.AcademyVideo,
        PromoBoxType.Benefit,
        PromoBoxType.Event,
        PromoBoxType.News,
      ].includes(item.type)
    );

    filteredChosenItems.forEach((item) => {
      cookieValue[item.type].push(item.id);
    });

    return cookieValue;
  }

  private streamErrorWrapper<T>(stream$: Observable<T>, type: PromoBoxType): Observable<T> {
    return stream$.pipe(
      catchError((error) => {
        console.error(`Error fetching ${type} data for promo box:`, error);
        return of([] as T);
      })
    );
  }
}
